import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface Sequencer {
  id: string;
  userId: string;
  displayName: string | null;
  username: string | null;
  avatarUrl: string | null;
  sequenceCount: number;
}

interface UseTopSequencersOptions {
  holiday?: string;
  software?: string;
  limit?: number;
}

interface UseTopSequencersResult {
  sequencers: Sequencer[];
  isLoading: boolean;
  error: string | null;
}

/**
 * Custom hook to fetch top sequencers (creators) filtered by holiday or software type.
 *
 * Fetches sequences matching the filters, groups by user_id, counts sequences per user,
 * then joins with user_profiles to get creator information (display_name, username, avatar_url).
 *
 * Returns top N sequencers sorted by sequence count descending.
 *
 * @param options - Optional filters: holiday, software, limit (default 8)
 * @returns Sequencers array with loading and error states
 */
export function useTopSequencers(options: UseTopSequencersOptions = {}): UseTopSequencersResult {
  const { holiday, software, limit = 8 } = options;
  const [sequencers, setSequencers] = useState<Sequencer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Skip if neither filter is provided
    if (!holiday && !software) {
      setIsLoading(false);
      setSequencers([]);
      return;
    }

    const loadTopSequencers = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Build query for sequences with filters
        let sequencesQuery = supabase
          .from('sequences')
          .select('user_id')
          .not('user_id', 'is', null); // Exclude sequences without a user

        // Apply optional filters
        if (holiday) {
          sequencesQuery = sequencesQuery.eq('holiday_type', holiday);
        }
        if (software) {
          sequencesQuery = sequencesQuery.eq('software_type', software);
        }

        const { data: sequenceData, error: sequenceError } = await sequencesQuery;

        if (sequenceError) {
          console.error('Sequences query error:', sequenceError);
          throw new Error(sequenceError.message);
        }

        if (!sequenceData || sequenceData.length === 0) {
          console.log('No sequences found for filters:', { holiday, software });
          setSequencers([]);
          setIsLoading(false);
          return;
        }

        // Count sequences per user
        const userSequenceCounts = new Map<string, number>();
        sequenceData.forEach((seq) => {
          if (seq.user_id) {
            const count = userSequenceCounts.get(seq.user_id) || 0;
            userSequenceCounts.set(seq.user_id, count + 1);
          }
        });

        // Get top N user IDs by sequence count
        const topUserIds = Array.from(userSequenceCounts.entries())
          .sort((a, b) => b[1] - a[1]) // Sort by count descending
          .slice(0, limit)
          .map(([userId]) => userId);

        if (topUserIds.length === 0) {
          console.log('No users found with sequences');
          setSequencers([]);
          setIsLoading(false);
          return;
        }

        // Fetch user_profiles for top users
        const { data: profileData, error: profileError } = await supabase
          .from('user_profiles')
          .select('id, user_id, display_name, username, avatar_url')
          .in('user_id', topUserIds);

        if (profileError) {
          console.error('User profiles query error:', profileError);
          throw new Error(profileError.message);
        }

        if (!profileData) {
          console.log('No user profiles found');
          setSequencers([]);
          setIsLoading(false);
          return;
        }

        // Combine profile data with sequence counts and maintain sort order
        const sequencersWithCounts: Sequencer[] = profileData
          .map((profile) => ({
            id: profile.id,
            userId: profile.user_id,
            displayName: profile.display_name,
            username: profile.username,
            avatarUrl: profile.avatar_url,
            sequenceCount: userSequenceCounts.get(profile.user_id) || 0,
          }))
          .sort((a, b) => b.sequenceCount - a.sequenceCount); // Re-sort to maintain order

        setSequencers(sequencersWithCounts);
        console.log(`Loaded ${sequencersWithCounts.length} top sequencers for filters:`, {
          holiday,
          software,
        });
      } catch (err) {
        console.error('Error loading top sequencers:', err);
        setError(err instanceof Error ? err.message : 'Failed to load top sequencers');
        setSequencers([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadTopSequencers();
  }, [holiday, software, limit]);

  return {
    sequencers,
    isLoading,
    error,
  };
}
